import React, { useEffect, useState } from 'react';
import { useParams, useSearchParams } from 'react-router-dom';
import { Download, CheckCircle2 } from 'lucide-react';
import { quotePublicAPI, fmtMoney, fmtDay } from '@/lib/financeAPI';

// What the client sees from the link in their quote email: read the proposal, then accept or decline. No login.
export default function QuotePublic() {
  const { token } = useParams();
  const [params] = useSearchParams();
  const preview = params.get('preview') === '1';
  const [data, setData] = useState(null);
  const [missing, setMissing] = useState(false);
  const [mode, setMode] = useState(null);           // 'accept' | 'decline'
  const [f, setF] = useState({ name: '', title: '', reason: '', agree: false });
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState('');
  const [done, setDone] = useState(null);

  const load = () => quotePublicAPI.get(token, preview).then((r) => setData(r.data)).catch(() => setMissing(true));
  useEffect(() => { load(); }, [token]); // eslint-disable-line react-hooks/exhaustive-deps

  if (missing) return <div className="min-h-screen bg-[#FBF8F2] flex items-center justify-center p-6 text-center text-[#5C6670]">This link isn't valid. Please check with whoever sent it.</div>;
  if (!data) return <div className="min-h-screen bg-[#FBF8F2] flex items-center justify-center text-[#5C6670]">Loading…</div>;

  const { business: b, quote: q } = data;
  const t = q.totals || {};
  const cur = q.currency;
  const accent = b.brand_color || '#A62B1F';
  const kind = q.kind === 'change_order' ? 'Change order' : 'Quote';
  const open = ['sent', 'viewed'].includes(q.status) && !preview;

  const submit = async () => {
    setError('');
    if (mode === 'accept' && (f.name.trim().length < 2 || !f.agree)) { setError('Type your full name and tick the box to accept.'); return; }
    setBusy(true);
    try {
      if (mode === 'accept') await quotePublicAPI.accept(token, { name: f.name, title: f.title });
      else await quotePublicAPI.decline(token, { name: f.name, reason: f.reason });
      setDone(mode); setMode(null); load();
    } catch (e) { setError(e?.response?.data?.detail || 'Something went wrong. Please try again.'); } finally { setBusy(false); }
  };

  return (
    <div className="min-h-screen bg-[#FBF8F2] py-6 px-4">
      <div className="max-w-3xl mx-auto">
        {q.latest_url && (
          <div className="mb-3 rounded-lg bg-amber-50 border border-amber-200 p-3 text-sm text-amber-900">There's a newer version of this {kind.toLowerCase()}. <a className="underline font-semibold" href={q.latest_url}>Open the latest version</a></div>
        )}
        {done === 'accept' && <div className="mb-3 rounded-lg bg-emerald-50 border border-emerald-200 p-3 text-sm text-emerald-900 flex items-center gap-2"><CheckCircle2 className="w-4 h-4" />Thank you, it's accepted. {b.business_name} has been told and will be in touch.</div>}
        {done === 'decline' && <div className="mb-3 rounded-lg bg-[#F3EFE7] border border-[#E3DDD1] p-3 text-sm text-[#33404A]">Thanks for letting us know. {b.business_name} has been told.</div>}
        <div className="flex justify-end mb-3">
          <a href={quotePublicAPI.pdfUrl(token)} className="inline-flex items-center gap-1.5 text-sm font-semibold px-4 py-2 rounded-md text-white" style={{ backgroundColor: accent }}><Download className="w-4 h-4" />Download PDF</a>
        </div>
        <div className="bg-white rounded-xl border border-[#E3DDD1] overflow-hidden">
          <div className="h-1.5" style={{ backgroundColor: accent }} />
          <div className="p-6 sm:p-8">
            <div className="flex flex-col sm:flex-row sm:justify-between gap-6">
              <div>
                {b.logo_url && <img src={b.logo_url} alt="" className="h-12 mb-3 object-contain" />}
                <div className="text-lg font-semibold text-[#1E2A33]">{b.business_name}</div>
                <div className="text-sm text-[#5C6670] whitespace-pre-line">{[b.address, b.email, b.phone, b.website].filter(Boolean).join('\n')}</div>
              </div>
              <div className="sm:text-right">
                <div className="text-3xl font-bold tracking-tight uppercase" style={{ color: accent }}>{kind}</div>
                <div className="text-sm text-[#5C6670] mt-1">No. <strong className="text-[#1E2A33]">{q.label}</strong></div>
                <div className="text-sm text-[#5C6670]">{fmtDay(q.issue_date)}</div>
                {q.valid_until && <div className="text-sm text-[#5C6670]">Valid until {fmtDay(q.valid_until)}</div>}
                {q.parent && <div className="text-sm text-[#5C6670]">Changes quote {q.parent.label}</div>}
              </div>
            </div>

            <div className="mt-6">
              <div className="text-xs uppercase tracking-wider font-semibold" style={{ color: accent }}>Prepared for</div>
              <div className="text-sm text-[#1E2A33] font-semibold mt-1">{q.client?.company || q.client?.name}</div>
              {q.client?.company && q.client?.name && q.client.name !== q.client.company && <div className="text-sm text-[#1E2A33]">{q.client.name}</div>}
            </div>

            {q.title && <h1 className="mt-6 text-2xl font-semibold text-[#1E2A33]" style={{ fontFamily: 'Fraunces, Georgia, serif' }}>{q.title}</h1>}
            {q.project_name && <div className="text-sm text-[#5C6670]">Project: {q.project_name}</div>}

            {(q.sections || []).filter((s) => (s.body || '').trim()).map((s, i) => (
              <div key={i} className="mt-6">
                <h2 className="text-base font-semibold mb-1" style={{ color: accent }}>{s.title}</h2>
                <div className="text-sm text-[#1E2A33] whitespace-pre-line leading-relaxed">{s.body}</div>
              </div>
            ))}

            <h2 className="mt-8 text-base font-semibold mb-2" style={{ color: accent }}>{q.kind === 'change_order' ? 'Changes' : 'Pricing'}</h2>
            <div className="overflow-x-auto">
              <table className="w-full text-sm">
                <thead><tr className="bg-[#F3EFE7] text-[11px] uppercase tracking-wider text-[#5C6670]"><th className="text-left p-2">Description</th><th className="text-right p-2">Qty</th><th className="text-right p-2">Rate</th><th className="text-right p-2">Amount</th></tr></thead>
                <tbody>{q.items.map((it, i) => (
                  <tr key={i} className="border-b border-[#F0EBE1]"><td className="p-2 whitespace-pre-line">{it.description}{it.tax && <span className="text-[11px] text-[#94A3B8] ml-1">({it.tax})</span>}</td>
                    <td className="p-2 text-right whitespace-nowrap">{Number(it.quantity)}{it.unit ? ` ${it.unit}` : ''}</td><td className="p-2 text-right whitespace-nowrap">{fmtMoney(it.unit_price, cur)}</td><td className="p-2 text-right whitespace-nowrap">{fmtMoney(t.lines?.[i], cur)}</td></tr>
                ))}</tbody>
              </table>
            </div>
            <div className="mt-4 ml-auto max-w-xs text-sm space-y-1">
              <div className="flex justify-between"><span className="text-[#5C6670]">Subtotal</span><span>{fmtMoney(t.subtotal, cur)}</span></div>
              {t.discount > 0 && <div className="flex justify-between"><span className="text-[#5C6670]">Discount</span><span>−{fmtMoney(t.discount, cur)}</span></div>}
              {(t.taxes || []).map((x) => <div key={x.id} className="flex justify-between"><span className="text-[#5C6670]">{x.name} ({x.rate}%)</span><span>{fmtMoney(x.amount, cur)}</span></div>)}
              <div className="flex justify-between font-semibold text-base border-t border-[#1E2A33] pt-1.5"><span>Total ({cur})</span><span>{fmtMoney(t.total, cur)}</span></div>
            </div>
            {q.notes && <div className="mt-6 text-sm text-[#1E2A33] whitespace-pre-line">{q.notes}</div>}

            {/* Decision */}
            <div className="mt-8 rounded-xl border border-[#E3DDD1] bg-[#FBF8F2] p-5">
              {q.accepted ? (
                <div className="flex items-center gap-2 text-emerald-800"><CheckCircle2 className="w-5 h-5" /><span>Accepted by <strong>{q.accepted.name}</strong>{q.accepted.title ? `, ${q.accepted.title}` : ''} on {new Date(q.accepted.at).toLocaleDateString('en-CA', { dateStyle: 'long' })}.</span></div>
              ) : q.status === 'declined' ? (
                <div className="text-sm text-[#5C6670]">This {kind.toLowerCase()} was declined. Contact {b.business_name} if you'd like an updated one.</div>
              ) : q.status === 'expired' ? (
                <div className="text-sm text-[#5C6670]">This {kind.toLowerCase()} expired on {fmtDay(q.valid_until)}. Contact {b.business_name} for an updated one.</div>
              ) : q.status === 'superseded' ? (
                <div className="text-sm text-[#5C6670]">This version has been replaced{q.latest_url ? <> by a <a className="underline" href={q.latest_url}>newer one</a></> : ''}.</div>
              ) : !open ? (
                <div className="text-sm text-[#5C6670]">Preview: the client accepts or declines here.</div>
              ) : mode ? (
                <div className="space-y-3">
                  <div className="font-semibold text-[#1E2A33]">{mode === 'accept' ? `Accept this ${kind.toLowerCase()}` : `Decline this ${kind.toLowerCase()}`}</div>
                  <div className="grid sm:grid-cols-2 gap-2">
                    <input className="h-10 rounded-md border border-[#E3DDD1] px-3 text-sm bg-white" placeholder="Your full name" value={f.name} onChange={(e) => setF({ ...f, name: e.target.value })} />
                    {mode === 'accept' && <input className="h-10 rounded-md border border-[#E3DDD1] px-3 text-sm bg-white" placeholder="Your title (optional)" value={f.title} onChange={(e) => setF({ ...f, title: e.target.value })} />}
                  </div>
                  {mode === 'decline' && <textarea className="w-full rounded-md border border-[#E3DDD1] p-3 text-sm bg-white" rows={3} placeholder="Anything we should know? (optional)" value={f.reason} onChange={(e) => setF({ ...f, reason: e.target.value })} />}
                  {mode === 'accept' && (
                    <label className="flex items-start gap-2 text-sm text-[#33404A]"><input type="checkbox" className="mt-1" checked={f.agree} onChange={(e) => setF({ ...f, agree: e.target.checked })} />
                      <span>I accept this {kind.toLowerCase()} ({fmtMoney(t.total, cur)}) and its terms on behalf of {q.client?.company || 'myself'}. Typing my name counts as my signature.</span></label>
                  )}
                  {error && <div className="text-sm text-[#A62B1F]">{error}</div>}
                  <div className="flex gap-2">
                    <button className="px-4 py-2 rounded-md text-white text-sm font-semibold disabled:opacity-50" style={{ backgroundColor: mode === 'accept' ? accent : '#5C6670' }} disabled={busy} onClick={submit}>{busy ? 'Sending…' : mode === 'accept' ? 'Accept' : 'Decline'}</button>
                    <button className="px-4 py-2 rounded-md text-sm text-[#5C6670]" onClick={() => { setMode(null); setError(''); }}>Cancel</button>
                  </div>
                </div>
              ) : (
                <div className="flex items-center gap-3 flex-wrap">
                  <div className="text-sm text-[#33404A] flex-1">Happy with this? Accept it here and {b.business_name} will be notified straight away.</div>
                  <button className="px-5 py-2.5 rounded-md text-white text-sm font-semibold" style={{ backgroundColor: accent }} onClick={() => setMode('accept')}>Accept {kind.toLowerCase()}</button>
                  <button className="px-3 py-2.5 rounded-md text-sm text-[#5C6670] underline" onClick={() => setMode('decline')}>Decline</button>
                </div>
              )}
            </div>
          </div>
        </div>
        <p className="text-center text-xs text-[#94A3B8] mt-4">Sent with Stagehead OS</p>
      </div>
    </div>
  );
}
